/**
 * datos-personales.ts — Web data-layer for the researcher's OWN personal data,
 * used outside the edit shell (today: the Preferencias hub, which needs to know
 * whether there is a phone on file and to add one without sending the person to
 * the edit page).
 *
 * `EditShell` talks to these same endpoints inline because it juggles the whole
 * editing state; this module covers the small read/create that other surfaces
 * need, so they don't reimplement the revalidation dance.
 */

import { apiGet, apiPost } from '@/lib/apiClient';
import { runWithInvestigatorProfileRevalidation } from '@/lib/investigator-revalidation-client';
import { TELEFONO_CODIGO } from '@/lib/telefono';

// El formateo vive en `telefono.ts` (módulo puro) porque lo importa también el
// adaptador público, que corre en el servidor. Se re-exporta acá para que quien
// ya trabaja con datos personales lo encuentre a mano.
export { TELEFONO_CODIGO, formatearTelefonoChile, formatearDatoPersonal } from '@/lib/telefono';

export interface DatoPersonalResumen {
	codigo: string;
	valorTexto: string;
	esPublico: boolean;
}

interface DatosPersonalesPayload {
	datos: DatoPersonalResumen[];
}

interface TiposPayload {
	tipos: Array<{ id: number; codigo: string }>;
}

/** GET /investigadores/:slug/datos-personales — sólo el dueño (o un admin) los ve. */
export async function getDatosPersonales(slug: string): Promise<DatoPersonalResumen[]> {
	const res = await apiGet<DatosPersonalesPayload>(
		`/investigadores/${encodeURIComponent(slug)}/datos-personales`,
	);
	return res.datos ?? [];
}

/** El teléfono cargado, o `null` si no hay ninguno con valor. */
export function telefonoDe(datos: DatoPersonalResumen[]): string | null {
	const fila = datos.find((d) => d.codigo === TELEFONO_CODIGO && d.valorTexto.trim().length > 0);
	return fila ? fila.valorTexto.trim() : null;
}

/** Id del tipo de dato en el catálogo, o `null` si ese código no existe. */
export async function getIdTipoDato(codigo: string): Promise<number | null> {
	const res = await apiGet<TiposPayload>('/investigadores/catalogos/tipos-dato-personal');
	return res.tipos.find((t) => t.codigo === codigo)?.id ?? null;
}

/**
 * Agrega un dato personal. Pasa por `runWithInvestigatorProfileRevalidation`
 * porque un dato PÚBLICO cambia el perfil cacheado; con `esPublico` en false la
 * revalidación es un no-op barato y evita tener que acordarse de la excepción.
 */
export async function agregarDatoPersonal(
	slug: string,
	payload: { idTipoDatoPersonal: number; valorTexto: string; esPublico: boolean },
): Promise<DatoPersonalResumen[]> {
	return runWithInvestigatorProfileRevalidation(slug, async () => {
		const res = await apiPost<DatosPersonalesPayload>(
			`/investigadores/${encodeURIComponent(slug)}/datos-personales`,
			payload,
		);
		return res.datos ?? [];
	});
}
